/**
 * The customer sign-in form's state machine, as a pure reducer.
 *
 * Authority: bead scope-r3x5.
 *
 * Split out from `CustomerAuthProvider.tsx` on the same discipline
 * `panelState.ts` and `visibility.ts` document next to their own components
 * (CLAUDE.md § Quality gates, and testability without a DOM — this project's
 * `vitest.config.ts` runs `test/**\/*.test.ts` under `environment: 'node'`).
 */

import type { CustomerSessionProbe } from './customer-session.ts'

export type CustomerSignInState =
  | { readonly step: 'checking' }
  | { readonly step: 'email'; readonly pending: boolean; readonly error: string | null }
  | {
      readonly step: 'code'
      readonly email: string
      readonly resendAfterSeconds: number
      readonly pending: boolean
      readonly error: string | null
    }
  /**
   * Verified, but not yet bound to an engagement (IMPL-ENGAGE-002 §8.2).
   *
   * Distinct from `signed-in` because the credential is different — a
   * short-lived selection token, not a session — and so is what may be
   * rendered under it: the chooser, and nothing that reads engagement data.
   */
  | { readonly step: 'choosing' }
  | { readonly step: 'signed-in' }

export type CustomerSignInAction =
  | { readonly type: 'SESSION_CHECKED'; readonly probe: CustomerSessionProbe }
  | { readonly type: 'CODE_REQUEST_STARTED' }
  | { readonly type: 'CODE_REQUESTED'; readonly email: string; readonly resendAfterSeconds: number }
  | { readonly type: 'CODE_REQUEST_FAILED'; readonly message: string }
  | { readonly type: 'CODE_VERIFY_STARTED' }
  | { readonly type: 'CODE_VERIFIED'; readonly selectionRequired: boolean }
  | { readonly type: 'CODE_VERIFY_FAILED'; readonly message: string }
  /** A reference was redeemed for an engagement-bound session (§8.3). */
  | { readonly type: 'ENGAGEMENT_SELECTED' }

export const INITIAL_CUSTOMER_SIGN_IN_STATE: CustomerSignInState = { step: 'checking' }

export function customerSignInReducer(
  state: CustomerSignInState,
  action: CustomerSignInAction,
): CustomerSignInState {
  switch (action.type) {
    case 'SESSION_CHECKED':
      if (action.probe === 'signed-in') return { step: 'signed-in' }
      if (action.probe === 'choosing') return { step: 'choosing' }

      return { step: 'email', pending: false, error: null }
    case 'CODE_REQUEST_STARTED':
      return state.step === 'email' || state.step === 'code'
        ? { ...state, pending: true, error: null }
        : state
    case 'CODE_REQUESTED':
      return {
        step: 'code',
        email: action.email,
        resendAfterSeconds: action.resendAfterSeconds,
        pending: false,
        error: null,
      }
    case 'CODE_REQUEST_FAILED':
      return state.step === 'email' || state.step === 'code'
        ? { ...state, pending: false, error: action.message }
        : state
    case 'CODE_VERIFY_STARTED':
      return state.step === 'code' ? { ...state, pending: true, error: null } : state
    case 'CODE_VERIFIED':
      return action.selectionRequired ? { step: 'choosing' } : { step: 'signed-in' }
    /*
     * `choosing` had no exit. `ChoosingGate` sends every path outside the
     * chooser back to it, so an owner who signed in reaching more than one
     * engagement, picked one, and then opened the questionnaire was returned
     * to the list — for the rest of the session, with no way out, because
     * picking again landed in exactly the same state.
     */
    case 'ENGAGEMENT_SELECTED':
      return { step: 'signed-in' }
    case 'CODE_VERIFY_FAILED':
      return state.step === 'code' ? { ...state, pending: false, error: action.message } : state
    default:
      return state
  }
}
